import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Users, Plus, Save, Trash2, Shield, Mail, KeyRound } from 'lucide-react';

/**
 * Gestor de colaboradores Right Hand (master_assistant) del SuperMaster.
 * Permite crear colaboradores, togglear permisos SaaS y qué tenants pueden facturar.
 */
export default function RightHandManager() {
  const [perms, setPerms] = useState([]);
  const [list, setList] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [newUser, setNewUser] = useState({ email: '', full_name: '', password: '', permissions: [], allowed_tenants: [] });
  const [editing, setEditing] = useState(null); // user_id en edición

  const load = async () => {
    try {
      const [p, l, t] = await Promise.all([
        api.getRightHandPermsCatalog(),
        api.listRightHand(),
        api.getTenants().catch(() => ({ data: [] })),
      ]);
      setPerms(p.data.permissions || []);
      setList(l.data || []);
      setTenants(t.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error al cargar'); }
  };
  useEffect(() => { load(); }, []);

  const togglePerm = (target, setter, key) => {
    const cur = target.permissions || target.saas_permissions || [];
    const has = cur.includes(key);
    const next = has ? cur.filter((x) => x !== key) : [...cur, key];
    setter({ ...target, permissions: next, saas_permissions: next });
  };
  const toggleTenant = (target, setter, tid) => {
    const cur = target.allowed_tenants || target.allowed_tenants_billing || [];
    const has = cur.includes(tid);
    const next = has ? cur.filter((x) => x !== tid) : [...cur, tid];
    setter({ ...target, allowed_tenants: next, allowed_tenants_billing: next });
  };

  const create = async () => {
    if (!newUser.email || !newUser.full_name || !newUser.password) {
      toast.error('SalesPRO: Correo, nombre y contraseña son obligatorios'); return;
    }
    try {
      await api.createRightHand({
        email: newUser.email.trim().toLowerCase(),
        full_name: newUser.full_name.trim(),
        password: newUser.password,
        permissions: newUser.permissions,
        allowed_tenants: newUser.allowed_tenants,
      });
      toast.success('SalesPRO: Colaborador creado');
      setNewUser({ email: '', full_name: '', password: '', permissions: [], allowed_tenants: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };

  const save = async (u) => {
    try {
      await api.updateRightHand(u.user_id, {
        full_name: u.full_name,
        permissions: u.saas_permissions || [],
        allowed_tenants: u.allowed_tenants_billing || [],
        active: u.active !== false,
      });
      toast.success('SalesPRO: Permisos actualizados');
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };

  const resetPwd = async (u) => {
    const pwd = window.prompt(`Nueva contraseña para ${u.email}:`);
    if (!pwd) return;
    try { await api.updateRightHand(u.user_id, { password: pwd }); toast.success('SalesPRO: Contraseña actualizada'); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar al colaborador ${u.full_name}?`)) return;
    try { await api.deleteRightHand(u.user_id); toast.success('SalesPRO: Colaborador eliminado'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };

  return (
    <Card data-testid="right-hand-manager">
      <CardContent className="p-5 space-y-4">
        <div>
          <h3 className="font-heading font-bold text-[#022C22] flex items-center gap-2"><Users size={18} /> Mano derecha (colaboradores Super Master)</h3>
          <p className="text-xs text-[#64748B]">Crea colaboradores con acceso <b>sólo</b> al panel de administración SalesPRO. NUNCA ven inventario, clientes ni ventas de las empresas afiliadas.</p>
        </div>

        {/* Crear nuevo colaborador */}
        <div className="rounded-lg border bg-[#F8FAFC] p-3 space-y-3" data-testid="rh-new-form">
          <p className="text-sm font-semibold text-[#022C22] flex items-center gap-1"><Plus size={14} /> Nuevo colaborador</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <div><Label className="text-xs">Correo</Label><Input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="rh-new-email" /></div>
            <div><Label className="text-xs">Nombre completo</Label><Input value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} data-testid="rh-new-name" /></div>
            <div><Label className="text-xs">Contraseña inicial</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="rh-new-password" /></div>
          </div>
          <details>
            <summary className="text-xs text-[#334155] cursor-pointer">Permisos SaaS (expandir)</summary>
            <div className="grid sm:grid-cols-2 gap-1 mt-2 text-xs">
              {perms.map((p) => (
                <label key={p.key} className="flex items-center gap-2 py-1">
                  <Switch checked={newUser.permissions.includes(p.key)} onCheckedChange={() => togglePerm(newUser, setNewUser, p.key)} data-testid={`rh-new-perm-${p.key}`} />
                  {p.label}
                </label>
              ))}
            </div>
          </details>
          <details>
            <summary className="text-xs text-[#334155] cursor-pointer">Tenants que puede facturar ({newUser.allowed_tenants.length}) (expandir)</summary>
            <div className="flex flex-wrap gap-1 mt-2">
              {tenants.map((t) => (
                <button key={t.tenant_id} type="button" onClick={() => toggleTenant(newUser, setNewUser, t.tenant_id)}
                  className={`text-[11px] px-2 py-1 rounded border ${newUser.allowed_tenants.includes(t.tenant_id) ? 'bg-[#022C22] text-white border-[#022C22]' : 'bg-white border-[#E2E8F0] text-[#475569]'}`}
                  data-testid={`rh-new-tenant-${t.tenant_id}`}>
                  {newUser.allowed_tenants.includes(t.tenant_id) ? '✓ ' : ''}{t.name}
                </button>
              ))}
            </div>
          </details>
          <Button onClick={create} data-testid="rh-new-create"><Plus size={14} className="mr-1" /> Crear colaborador</Button>
        </div>

        {/* Lista de colaboradores */}
        <div className="space-y-2">
          {list.length === 0 && <p className="text-xs text-[#94A3B8]">Aún no has creado colaboradores.</p>}
          {list.map((u) => {
            const isEdit = editing === u.user_id;
            return (
              <div key={u.user_id} className="rounded-lg border p-3 space-y-2" data-testid={`rh-item-${u.user_id}`}>
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <div>
                    <p className="text-sm font-semibold text-[#022C22]">{u.full_name} <span className="text-xs text-[#64748B]">· {u.email}</span></p>
                    <p className="text-[11px] text-[#64748B]">
                      <Shield size={10} className="inline" /> {(u.saas_permissions || []).length} permiso(s) · {(u.allowed_tenants_billing || []).length} tenant(s) facturables · {u.active === false ? '🔴 inactivo' : '🟢 activo'}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {!isEdit && <Button size="sm" variant="outline" onClick={() => setEditing(u.user_id)} data-testid={`rh-edit-${u.user_id}`}>Editar</Button>}
                    {isEdit && <Button size="sm" onClick={() => save(u)} data-testid={`rh-save-${u.user_id}`}><Save size={12} className="mr-1" /> Guardar</Button>}
                    <Button size="sm" variant="outline" onClick={() => resetPwd(u)} title="Nueva contraseña"><KeyRound size={12} /></Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => remove(u)}><Trash2 size={12} /></Button>
                  </div>
                </div>

                {isEdit && (
                  <div className="space-y-2 bg-[#F8FAFC] rounded p-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Activo</Label>
                      <Switch checked={u.active !== false} onCheckedChange={(v) => setList(list.map((x) => x.user_id === u.user_id ? { ...x, active: v } : x))} />
                    </div>
                    <p className="text-xs font-semibold text-[#022C22]">Permisos SaaS</p>
                    <div className="grid sm:grid-cols-2 gap-1 text-xs">
                      {perms.map((p) => (
                        <label key={p.key} className="flex items-center gap-2 py-1">
                          <Switch checked={(u.saas_permissions || []).includes(p.key)} onCheckedChange={() => setList(list.map((x) => {
                            if (x.user_id !== u.user_id) return x;
                            const cur = x.saas_permissions || [];
                            return { ...x, saas_permissions: cur.includes(p.key) ? cur.filter((k) => k !== p.key) : [...cur, p.key] };
                          }))} data-testid={`rh-perm-${u.user_id}-${p.key}`} />
                          {p.label}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs font-semibold text-[#022C22] mt-2">Tenants que puede facturar</p>
                    <div className="flex flex-wrap gap-1">
                      {tenants.map((t) => (
                        <button key={t.tenant_id} type="button" onClick={() => setList(list.map((x) => {
                          if (x.user_id !== u.user_id) return x;
                          const cur = x.allowed_tenants_billing || [];
                          return { ...x, allowed_tenants_billing: cur.includes(t.tenant_id) ? cur.filter((k) => k !== t.tenant_id) : [...cur, t.tenant_id] };
                        }))}
                          className={`text-[11px] px-2 py-1 rounded border ${(u.allowed_tenants_billing || []).includes(t.tenant_id) ? 'bg-[#022C22] text-white border-[#022C22]' : 'bg-white border-[#E2E8F0] text-[#475569]'}`}>
                          {(u.allowed_tenants_billing || []).includes(t.tenant_id) ? '✓ ' : ''}{t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
